import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

// Wave 2 — Button variants aligned to design tokens.
// Semantic (new): primary | secondary | ghost | destructive
// Legacy (kept for shadcn primitives — calendar, pagination, alert-dialog, carousel):
//   default (= primary-ish via shadcn HSL vars), outline, link
// Sizes: sm | md | lg (semantic) + default | icon (legacy)
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors duration-fast ease-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Semantic (Wave 2)
        primary:     "bg-brand-cyan text-surface-canvas font-semibold hover:brightness-110 active:brightness-95 shadow-sm",
        secondary:   "bg-surface-card-hover text-foreground border border-subtle hover:border-strong hover:bg-surface-overlay",
        ghost:       "bg-transparent text-foreground hover:bg-surface-card-hover",
        destructive: "bg-status-error text-white font-semibold hover:brightness-110 active:brightness-95 shadow-sm",
        // Premium tech (UI-1.5) — gradient cyan→emerald + glow + scale.
        // Coexiste com `primary` — usar pra CTA principal de hero apenas.
        // Pulse opcional via classe utilitária `pulse-glow` (em index.css).
        "primary-tech":
          "bg-gradient-to-r from-[#00D4FF] via-[#00B8E5] to-[#00FFA3] text-white font-medium " +
          "border border-[rgba(0,212,255,0.4)] " +
          "shadow-[0_0_24px_rgba(0,212,255,0.4)] " +
          "hover:shadow-[0_0_40px_rgba(0,212,255,0.6)] hover:scale-[1.02] " +
          "active:scale-[0.99] transition-all duration-200 relative overflow-hidden",
        // Legacy shadcn (kept for back-compat)
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        outline: "border border-subtle bg-transparent hover:bg-surface-card-hover hover:text-foreground",
        link:    "text-brand-cyan underline-offset-4 hover:underline",
      },
      size: {
        // Semantic (Wave 2)
        sm: "h-8 px-3 text-xs rounded-md",
        md: "h-9 px-4 text-sm rounded-md",
        lg: "h-11 px-6 text-sm rounded-lg",
        // Legacy
        default: "h-9 px-4 py-2 text-sm",
        icon:    "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",  // keep legacy default so existing calendar/pagination look unchanged
      size:    "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
